import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Button,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  IconButton,
  Text,
  Flex,
  Tooltip,
} from "@chakra-ui/react";
import { AddIcon, SettingsIcon } from "@chakra-ui/icons";
import { FiChevronLeft, FiChevronRight, FiDownload } from "react-icons/fi";
import exportToExcelsss, { handleSaveData } from "./utils/functions";
import { MdEmail } from "react-icons/md";
import MailModal from "./MailModal";

// Define the type for FieldData, including original and edited values
interface FieldData {
  originalValues: any;
  editedValues: string | null;
}

// Define the RowData type for rows that can contain multiple fields
interface RowData {
  id: number;
  [key: string]: FieldData | number;
}

// Transform the data to wrap each field in { originalValues, editedValues }
const transformData = (pages: any[]) => {
  if (!pages || pages.length === 0 || !pages[0].fields) {
    console.error("Invalid pages data or missing 'fields' property");
    return [];
  }

  return pages
    .map((page: any, pageIndex: number) => {
      return Object.entries(page.fields).map(([fieldName, fieldData]) => {
        if (Array.isArray(fieldData)) {
          const rows = fieldData.map((item: any, index: number) => {
            const transformedRow: any = { id: index };
            for (const [key, value] of Object.entries(item)) {
              transformedRow[key] = {
                originalValues: (value as any)?.originalValues || null,
                editedValues: (value as any)?.editedValues,
              };
            }
            return transformedRow;
          });
          return { fieldName, rows, pageIndex };
        }
        return null;
      });
    })
    .flat()
    .filter(Boolean);
};

const EditableTable = forwardRef(
  (
    {
      pages,
      name,
      workFlowData,
      accountDetails,
      setCurrentTotalDataCount,
    }: {
      pages: any[];
      name: string;
      workFlowData: any;
      accountDetails: any;
      setCurrentTotalDataCount?: any;
    },
    ref
  ) => {
    const [fileData, setFileData] = useState<any>(null);
    const [isMailOpen, setIsMailOpen] = useState<boolean>(false);
    const [fieldName, setFieldName] = useState<string[]>([]);

    const [data, setData] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
      if (pages && pages.length > 0) {
        const transformedData: any = transformData(pages);
        setData(transformedData);
        setTotalPages(pages.length);
        const totalDataCountForCurrentPage =
          transformedData.find(
            ({ pageIndex }: any) => pageIndex === currentPage
          )?.rows.length || 0;

        if (setCurrentTotalDataCount) {
          setCurrentTotalDataCount(totalDataCountForCurrentPage);
        }
      }
    }, [pages]);

    const handleCloseMail = () => {
      setIsMailOpen(false);
    };

    // Handle edit change for the selected page
    const handleEditChange = (
      fieldName: string,
      id: number,
      field: string,
      value: string
    ) => {
      setData((prevData) =>
        prevData.map(({ fieldName: currentFieldName, rows, pageIndex }) => {
          // Check if the fieldName and pageIndex match
          if (currentFieldName === fieldName && pageIndex === currentPage) {
            const updatedRows = rows.map((row: any) => {
              // Find the specific row by id
              if (row.id === id) {
                const currentField = row[field];

                // If the value is being deleted (i.e., an empty string), ensure we set it to "" or null explicitly
                const updatedField =
                  value !== ""
                    ? { ...currentField, editedValues: value }
                    : { ...currentField, editedValues: "" };

                return { ...row, [field]: updatedField };
              }
              return row;
            });
            return {
              fieldName: currentFieldName,
              rows: updatedRows,
              pageIndex,
            };
          }
          return { fieldName: currentFieldName, rows, pageIndex };
        })
      );
      const totalDataCountForCurrentPage =
        data.find(({ pageIndex }) => pageIndex === currentPage)?.rows.length ||
        0;

      if (setCurrentTotalDataCount) {
        setCurrentTotalDataCount(totalDataCountForCurrentPage);
      }
    };

    // Add a new row to the selected page
    const handleAddRow = (
      fieldName: string,
      index: number,
      position: "before" | "after"
    ) => {
      setData((prevData) =>
        prevData.map(({ fieldName: currentFieldName, rows, pageIndex }) => {
          if (currentFieldName === fieldName && pageIndex === currentPage) {
            const newRow: RowData = { id: Date.now() };
            Object.keys(rows[0]).forEach((key) => {
              newRow[key] = { originalValues: null, editedValues: null };
            });

            const updatedRows = [...rows];
            if (position === "before") {
              updatedRows.splice(index, 0, newRow);
            } else {
              updatedRows.splice(index + 1, 0, newRow);
            }

            return {
              fieldName: currentFieldName,
              rows: updatedRows,
              pageIndex,
            };
          }
          return { fieldName: currentFieldName, rows, pageIndex };
        })
      );
    };

    // Delete a row from the selected page
    const handleDeleteRow = (fieldName: string, index: number) => {
      setData((prevData) =>
        prevData.map(({ fieldName: currentFieldName, rows, pageIndex }) => {
          if (currentFieldName === fieldName && pageIndex === currentPage) {
            const updatedRows = rows.filter(
              (row: any) => row.id !== rows[index].id
            );
            return {
              fieldName: currentFieldName,
              rows: updatedRows,
              pageIndex,
            };
          }
          return { fieldName: currentFieldName, rows, pageIndex };
        })
      );
    };

    useImperativeHandle(ref, () => ({
      save: handleSaveData(data),
    }));

    const handleOpenMail = () => {
      setIsMailOpen(true);
    };

    // Switch between pages
    const handlePageChange = (direction: "next" | "prev") => {
      if (direction === "next" && currentPage < totalPages - 1) {
        setCurrentPage((prevPage) => prevPage + 1);
      } else if (direction === "prev" && currentPage > 0) {
        setCurrentPage((prevPage) => prevPage - 1);
      }
    };

    const nameMapping: any = {
      name: "DRUG NAME",
      dosage: "DRUG CODE",
      quantity: "QUANTITY",
      UNIT_PRICE: "UNIT PRICE",
      buy_X: "BUY X",
      get_Y: "GET Y",
    };

    const columnOrder = ["name", "quantity", "UNIT_PRICE", "buy_X", "get_Y"];

    return (
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        p={2}
        boxShadow="lg"
        // bg="gray.50"
        mb={4}
      >
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <Flex justifyContent="center" alignItems="center">
            <Text fontSize="sm" color="teal.600">
              Page {currentPage + 1} of {totalPages}
            </Text>
          </Flex>
          <HStack>
            {workFlowData && workFlowData?.table[0]?.action?.downloadExcel && (
              <Button
                onClick={() => {
                  exportToExcelsss(handleSaveData(data), true);
                  // exportToExcel(handleSaveAllPages())
                }}
                display={name !== "Pending Approval" ? undefined : "none"}
                colorScheme="teal"
                variant="solid"
                size="sm"
                fontWeight="medium"
                _hover={{ bg: "teal.600", color: "white" }}
                leftIcon={<FiDownload />}
              >
                <Tooltip
                  label="Download all pages as Excel"
                  aria-label="Download Tooltip"
                  hasArrow
                >
                  Download
                </Tooltip>
              </Button>
            )}
            {workFlowData && true && (
              <Button
                display={name !== "Pending Approval" ? undefined : "none"}
                onClick={() => {
                  handleSaveData(data);
                  handleOpenMail();
                  // handleDownloadExcel(
                  //   transformedData,
                  //   values,
                  //   toastNotify,
                  //   sendMail,
                  //   fieldName,
                  //   fileData,
                  //   setFileData,
                  //   setFinalResults
                  // );
                  // handleOpenMail();
                  // exportToExcel(handleSaveAllPages())
                }}
                colorScheme="teal"
                variant="solid"
                size="sm"
                fontWeight="medium"
                _hover={{ bg: "teal.600", color: "white" }}
                leftIcon={<MdEmail />}
              >
                <Tooltip
                  label="Send mail with attachment."
                  aria-label="Send Mail"
                  hasArrow
                >
                  Send Mail
                </Tooltip>
              </Button>
            )}
            <Button
              onClick={() => handlePageChange("prev")}
              isDisabled={currentPage === 0}
              colorScheme="teal"
              variant="outline"
              size="sm"
              fontWeight="medium"
              _hover={{ bg: "teal.50" }}
            >
              <FiChevronLeft size={20} />
            </Button>

            <Input
              type="number"
              value={currentPage + 1} // Showing the page in 1-indexed format
              onChange={(e: any) => {
                const pageNumber = Math.max(
                  0,
                  Math.min(totalPages - 1, Number(e.target.value) - 1)
                ); // Ensuring page number is within bounds
                setCurrentPage(pageNumber);
              }}
              placeholder={`Enter page (1-${totalPages})`}
              w="60px"
              textAlign="center"
              fontSize="sm"
              borderColor="teal.400"
              focusBorderColor="teal.500"
              size="sm"
              mb={0}
            />

            <Button
              onClick={() => handlePageChange("next")}
              isDisabled={currentPage === totalPages}
              colorScheme="teal"
              variant="outline"
              size="sm"
              fontWeight="medium"
              _hover={{ bg: "teal.50" }}
            >
              <FiChevronRight size={20} />
            </Button>
          </HStack>
        </Flex>
        {/* Accordion to handle pages */}
        <Accordion allowMultiple defaultIndex={0}>
          {data
            .filter(({ pageIndex }) => pageIndex === currentPage)
            .map(({ fieldName, rows }, index) => {
              return (
                <AccordionItem key={index}>
                  <AccordionButton
                    _expanded={{ bg: "blue.50", color: "blue.700" }}
                  >
                    <Box
                      flex="1"
                      textAlign="left"
                      fontWeight="bold"
                      fontSize="md"
                    >
                      {fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}
                      {`(${data.find(({ pageIndex }) => pageIndex === currentPage)?.rows
                .length || 0})`}
                    </Box>
                    {/* <Box
                    flex="1"
                    textAlign="left"
                    fontWeight="bold"
                    color="teal.600"
                  >

                  </Box> */}
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel p={0}>
                    <Box
                      overflowX={{
                        base: "scroll",
                        md: "scroll",
                        lg: "visible",
                      }}
                      mt={2}
                    >
                      <Table variant="striped" size="sm" className="medicine-table">
                        <Thead>
                          <Tr>
                            <Th>SL.No.</Th>
                            {columnOrder
                              .filter((key) => key in rows[0]) // Ensure the key exists in the row
                              .map((key) => (
                                <Th textAlign={key === "name" ? "left" :"center"} key={key}>{nameMapping[key]}</Th>
                              ))}
                            {name === "Pending Approval" && <Th></Th>}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {rows.map((row: any, rowIndex: number) => (
                            <Tr key={row.id}>
                              <Td textAlign="center" fontWeight="bold">
                                {rowIndex + 1}
                              </Td>
                              {columnOrder
                                .filter((key) => key in row) // Ensure the key exists in the row
                                .map((key) => {
                                  const field = row[key]; // Extract the field
                                  return (
                                    <Td key={`${key}-${rowIndex}-${row.id}`}>
                                      <Input
                                        isReadOnly={name !== "Pending Approval"}
                                        type={key === "name" ? "text" : "number"}
                                        value={
                                          field?.editedValues ??
                                          field?.originalValues ??
                                          ""
                                        }
                                        textAlign={key === "name" ? "left" : key === "UNIT_PRICE" ? "right" : "center"}
                                        width={{
                                          base:
                                            key === "name" ? "220px" : key === "UNIT_PRICE" ? "110px" : "120px",
                                            md: key === "name" ? "220px" : key === "UNIT_PRICE" ? "110px"  : "100%",
                                            lg: key === "name" ? "260px" : key === "UNIT_PRICE" ? "110px" : "100%",
                                        }}
                                        style={{
                                          border: field?.editedValues
                                            ? "2px solid red"
                                            : "1px solid gray",
                                        }}
                                        onChange={(e) =>
                                          handleEditChange(
                                            fieldName,
                                            row.id,
                                            key,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </Td>
                                  );
                                })}
                              {name === "Pending Approval" && (
                                <Td>
                                  <Tooltip
                                    label="Add Data"
                                    hasArrow
                                    placement="left"
                                    zIndex="popover"
                                  >
                                    <HStack spacing={2} justify="center">
                                      <Popover>
                                        <PopoverTrigger>
                                          <IconButton
                                            icon={<AddIcon />}
                                            aria-label="Row Actions"
                                            size="sm"
                                            fontWeight={700}
                                            colorScheme="teal"
                                          />
                                        </PopoverTrigger>
                                        <PopoverContent zIndex="tooltip">
                                          <PopoverArrow />
                                          <PopoverCloseButton />
                                          <Box textAlign="center" mb={2} mt={3}>
                                            <Text
                                              fontSize="sm"
                                              fontWeight="bold"
                                              color="teal.600"
                                            >
                                              Manage Row Actions
                                            </Text>
                                          </Box>
                                          <PopoverBody>
                                            <Button
                                              size="sm"
                                              colorScheme="green"
                                              onClick={() =>
                                                handleAddRow(
                                                  fieldName,
                                                  rowIndex,
                                                  "before"
                                                )
                                              }
                                              w="full"
                                            >
                                              Add Before
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="green"
                                              onClick={() =>
                                                handleAddRow(
                                                  fieldName,
                                                  rowIndex,
                                                  "after"
                                                )
                                              }
                                              w="full"
                                              mt={2}
                                            >
                                              Add After
                                            </Button>
                                            <Button
                                              size="sm"
                                              colorScheme="red"
                                              onClick={() =>
                                                handleDeleteRow(
                                                  fieldName,
                                                  rowIndex
                                                )
                                              }
                                              w="full"
                                              mt={2}
                                            >
                                              Delete Row
                                            </Button>
                                          </PopoverBody>
                                        </PopoverContent>
                                      </Popover>
                                    </HStack>
                                  </Tooltip>
                                </Td>
                              )}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
        </Accordion>
        <MailModal
          fileData={fileData}
          isOpen={isMailOpen}
          onClose={handleCloseMail}
          fieldName={fieldName}
          setFieldName={setFieldName}
          transformedData={exportToExcelsss(handleSaveData(data))}
          setFileData={setFileData}
          sendMail={["mail"]}
          // data={data}
        />
      </Box>
    );
  }
);

export default EditableTable;